import React, { createContext, useContext, useState, useEffect } from 'react';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS, ADMIN_PUBKEYS } from '../config/constants';
import { Event } from 'nostr-tools';
import { publishAdminConfig } from '../lib/nostr';
import { useAuth } from './AuthContext';

interface FeatureFlags {
    chatEnabled: boolean;
    flowEnabled: boolean;
    exploreEnabled: boolean;
    searchEnabled: boolean;
    profileEditing: boolean;
    bookmarksEnabled: boolean;
    zapsEnabled: boolean;
    eksiAgenda: boolean;
    externalResources: boolean;
}

interface AdminSettings {
    isolateCommunity: boolean;
    mutedUsers: string[]; // List of pubkeys to hide globally on this client
    customRelays: string[]; // Dynamically added relays
    deletedEventIds: string[]; // List of event IDs hidden/deleted by admin (Kind 5)
    bannedWords: string[]; // List of words to auto-hide
    dismissedReportIds: string[]; // List of report IDs ignored by admin
    authorizedBots: string[]; // List of pubkeys for authorized bots
    features: FeatureFlags;
}

interface AdminContextType {
    settings: AdminSettings;
    updateSettings: (updates: Partial<AdminSettings>) => void;
    isAdminOpen: boolean;
    setIsAdminOpen: (open: boolean) => void;
    muteUser: (pubkey: string) => void;
    unmuteUser: (pubkey: string) => void;
    deleteEventLocally: (id: string) => void;
    addBannedWord: (word: string) => void;
    removeBannedWord: (word: string) => void;
    dismissReport: (id: string) => void;
    toggleFeature: (feature: keyof FeatureFlags) => void;
    publishSettings: () => Promise<void>;
    isPublishing: boolean;
    lastSyncedAt: number | null;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<AdminSettings>(() => {
        const saved = localStorage.getItem('mavililer_admin_settings');
        const defaultSettings: AdminSettings = {
            isolateCommunity: true,
            mutedUsers: [],
            customRelays: [],
            deletedEventIds: [],
            bannedWords: [],
            dismissedReportIds: [],
            authorizedBots: [],
            features: {
                chatEnabled: true,
                flowEnabled: true,
                exploreEnabled: true,
                searchEnabled: true,
                profileEditing: true,
                bookmarksEnabled: true,
                zapsEnabled: true,
                eksiAgenda: true,
                externalResources: true
            }
        };
        return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
    });
    const [isAdminOpen, setIsAdminOpen] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
    const { sk } = useAuth();
    const pool = getNostrPool();

    // Subscribe to Global Admin Config
    useEffect(() => {
        const filter = {
            kinds: [30005],
            '#d': ['mavililer_config'],
            authors: ADMIN_PUBKEYS
        };

        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                // Parse the config from tags
                const configUpdates: any = {
                    features: {},
                    bannedWords: []
                };

                event.tags.forEach(tag => {
                    if (tag[0] === 'feature') {
                        const featureKey = tag[1];
                        const value = tag[2] === 'true';
                        configUpdates.features[featureKey] = value;
                    } else if (tag[0] === 'system' && tag[1] === 'isolateCommunity') {
                        configUpdates.isolateCommunity = tag[2] === 'true';
                    } else if (tag[0] === 'word') {
                        configUpdates.bannedWords.push(tag[1]);
                    } else if (tag[0] === 'bot') {
                        if (!configUpdates.authorizedBots) configUpdates.authorizedBots = [];
                        configUpdates.authorizedBots.push(tag[1]);
                    }
                });

                // Apply updates using functional set to avoid stale closures
                setSettings(prev => ({
                    ...prev,
                    ...configUpdates,
                    features: { ...prev.features, ...configUpdates.features }
                }));
                setLastSyncedAt(event.created_at);
            }
        });

        return () => sub.close();
    }, []);

    useEffect(() => {
        localStorage.setItem('mavililer_admin_settings', JSON.stringify(settings));
    }, [settings]);

    const updateSettings = (updates: Partial<AdminSettings>) => {
        setSettings((prev) => ({ ...prev, ...updates }));
    };

    const toggleFeature = (feature: keyof FeatureFlags) => {
        setSettings(prev => ({
            ...prev,
            features: {
                ...prev.features,
                [feature]: !prev.features[feature]
            }
        }));
    };

    const muteUser = (pubkey: string) => {
        if (settings.mutedUsers.includes(pubkey)) return;
        updateSettings({ mutedUsers: [...settings.mutedUsers, pubkey] });
    };

    const unmuteUser = (pubkey: string) => {
        updateSettings({ mutedUsers: settings.mutedUsers.filter(pk => pk !== pubkey) });
    };

    const deleteEventLocally = (id: string) => {
        if (settings.deletedEventIds.includes(id)) return;
        updateSettings({ deletedEventIds: [...settings.deletedEventIds, id] });
    };

    const addBannedWord = (word: string) => {
        if (settings.bannedWords.includes(word)) return;
        updateSettings({ bannedWords: [...settings.bannedWords, word] });
    };

    const removeBannedWord = (word: string) => {
        updateSettings({ bannedWords: settings.bannedWords.filter(w => w !== word) });
    };

    const dismissReport = (id: string) => {
        if (settings.dismissedReportIds.includes(id)) return;
        updateSettings({ dismissedReportIds: [...settings.dismissedReportIds, id] });
    };

    const publishSettings = async () => {
        if (!sk && !(window as any).nostr) {
            alert('Ayarları yayına almak için giriş yapmalısınız.');
            return;
        }

        setIsPublishing(true);
        try {
            await publishAdminConfig(settings, sk);
            alert('Ayarlar başarıyla tüm ağa yayınlandı.');
        } catch (error) {
            console.error('Publish error:', error);
            alert('Ayarlar yayınlanırken bir hata oluştu.');
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <AdminContext.Provider value={{
            settings,
            updateSettings,
            isAdminOpen,
            setIsAdminOpen,
            muteUser,
            unmuteUser,
            deleteEventLocally,
            addBannedWord,
            removeBannedWord,
            dismissReport,
            toggleFeature,
            publishSettings,
            isPublishing,
            lastSyncedAt
        }}>
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (context === undefined) {
        throw new Error('useAdmin must be used within an AdminProvider');
    }
    return context;
};
